import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

const TermsOfServicePage: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="legal-page-container">
            <div className="legal-page-content">
                <button className="back-button" onClick={() => navigate(-1)}>
                    ← Back
                </button>

                <h1>Terms of Service</h1>
                <p className="last-updated">Last Updated: November 22, 2025 • Version 1.0</p>

                <section>
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                        By accessing and using SeedDots ("the Platform"), you accept and agree to be bound by
                        these Terms of Service. If you do not agree to these terms, please do not use the Platform.
                    </p>
                </section>

                <section>
                    <h2>2. Eligibility</h2>
                    <p>
                        You must be at least 18 years old to use this Platform. By using the Platform, you represent
                        and warrant that you meet this age requirement.
                    </p>
                </section>

                <section>
                    <h2>3. Account Registration</h2>
                    <p>
                        To use certain features of the Platform, you must register for an account. You agree to:
                    </p>
                    <ul>
                        <li>Provide accurate, current, and complete information during registration</li>
                        <li>Maintain and promptly update your account information</li>
                        <li>Maintain the security of your password and account</li>
                        <li>Accept responsibility for all activities under your account</li>
                        <li>Notify us immediately of any unauthorized use of your account</li>
                    </ul>
                </section>

                <section>
                    <h2>4. User Conduct</h2>
                    <p>You agree NOT to:</p>
                    <ul>
                        <li>Use the Platform for any illegal or unauthorized purpose</li>
                        <li>Violate any laws in your jurisdiction</li>
                        <li>Infringe on intellectual property rights of others</li>
                        <li>Transmit any harmful code, viruses, or malware</li>
                        <li>Harass, abuse, or harm other users</li>
                        <li>Spam or send unsolicited communications</li>
                        <li>Impersonate any person or entity</li>
                        <li>Attempt to gain unauthorized access to the Platform</li>
                        <li>Interfere with or disrupt the Platform's operation</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Platform Features</h2>
                    <h3>5.1 Tasks and Points</h3>
                    <p>
                        SeedDots allows entrepreneurs to post tasks and testers to complete them for points.
                        Points have no monetary value and cannot be exchanged for cash.
                    </p>

                    <h3>5.2 Teams</h3>
                    <p>
                        Users can form teams for collaboration. By joining a team, you consent to sharing your
                        email address with team members as outlined in our Privacy Policy.
                    </p>

                    <h3>5.3 Pre-Release Projects</h3>
                    <p>
                        Users can express interest in pre-release projects. By doing so, you consent to sharing
                        your email address with the project owner for project-related communications.
                    </p>
                </section>

                <section>
                    <h2>6. Content Ownership and License</h2>
                    <h3>6.1 Your Content</h3>
                    <p>
                        You retain all rights to content you create on the Platform (tasks, surveys, profiles, etc.).
                        By posting content, you grant us a non-exclusive, worldwide, royalty-free license to use,
                        display, and distribute your content solely for operating and improving the Platform.
                    </p>

                    <h3>6.2 Our Content</h3>
                    <p>
                        The Platform and its original content, features, and functionality are owned by SeedDots
                        and are protected by international copyright, trademark, and other intellectual property laws.
                    </p>
                </section>

                <section>
                    <h2>7. Account Termination</h2>
                    <p>
                        We reserve the right to suspend or terminate your account at any time, with or without notice,
                        for conduct that we believe:
                    </p>
                    <ul>
                        <li>Violates these Terms of Service</li>
                        <li>Is harmful to other users or the Platform</li>
                        <li>Is unlawful or fraudulent</li>
                        <li>Otherwise creates liability for us</li>
                    </ul>
                    <p>
                        You may delete your account at any time through your account settings.
                    </p>
                </section>

                <section>
                    <h2>8. Disclaimer of Warranties</h2>
                    <p>
                        THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                        EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
                        FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                    </p>
                    <p>
                        We do not warrant that:
                    </p>
                    <ul>
                        <li>The Platform will be uninterrupted or error-free</li>
                        <li>Defects will be corrected</li>
                        <li>The Platform is free of viruses or harmful components</li>
                        <li>Results from using the Platform will be accurate or reliable</li>
                    </ul>
                </section>

                <section>
                    <h2>9. Limitation of Liability</h2>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, SeedDots SHALL NOT BE LIABLE FOR ANY INDIRECT,
                        INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
                        WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER
                        INTANGIBLE LOSSES.
                    </p>
                </section>

                <section>
                    <h2>10. Indemnification</h2>
                    <p>
                        You agree to indemnify and hold harmless SeedDots and its officers, directors, employees,
                        and agents from any claims, losses, damages, liabilities, and expenses (including attorneys'
                        fees) arising out of:
                    </p>
                    <ul>
                        <li>Your use of the Platform</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any rights of another party</li>
                        <li>Content you post on the Platform</li>
                    </ul>
                </section>

                <section>
                    <h2>11. Modifications to Service</h2>
                    <p>
                        We reserve the right to modify or discontinue, temporarily or permanently, the Platform
                        (or any part thereof) with or without notice. You agree that we shall not be liable to you
                        or any third party for any modification, suspension, or discontinuance of the Platform.
                    </p>
                </section>

                <section>
                    <h2>12. Changes to Terms</h2>
                    <p>
                        We may revise these Terms from time to time. We will notify users of material changes by
                        posting the updated Terms on this page and updating the "Last Updated" date. Your continued
                        use of the Platform after changes become effective constitutes acceptance of the revised Terms.
                    </p>
                </section>

                <section>
                    <h2>13. Governing Law</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of [Jurisdiction to be determined],
                        without regard to its conflict of law provisions.
                    </p>
                </section>

                <section>
                    <h2>14. Dispute Resolution</h2>
                    <p>
                        Any disputes arising out of or relating to these Terms or the Platform shall first be
                        attempted to be resolved through good-faith negotiations. If negotiations fail, disputes
                        may be resolved through binding arbitration or in courts of competent jurisdiction.
                    </p>
                </section>

                <section>
                    <h2>15. Contact Information</h2>
                    <p>
                        If you have any questions about these Terms, please contact us at:
                    </p>
                    <p className="contact-info">
                        Email: seeddots@outlook.com<br />
                    </p>
                </section>

                <section className="acknowledgment">
                    <p>
                        <strong>By using SeedDots, you acknowledge that you have read, understood, and agree to
                            be bound by these Terms of Service.</strong>
                    </p>
                </section>
            </div>
        </div>
    );
};

export default TermsOfServicePage;
